import { defineComponent as _defineComponent } from 'vue'
import { computed, getCurrentInstance, ref, watch, type PropType } from 'vue'
import { useMutation, useQuery } from '@vue/apollo-composable'
import { formatAsGermanDate } from '~/functions/helpers'
import { useBatchLockImpactsMutation, useGetImpactGoalLockStatesQuery } from '~/types/types'

type ImpactGoalLite = {
  id?: number | null
  ventureId?: string | null
  impacts?: Array<{
    id?: number | null
    isEditable?: boolean | null
    submitDate?: string | null
    concept?: { label?: string | null } | null
  }>
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ImpactLock',
  props: {
  value: {
    type: Boolean,
    required: true,
  },
  impactGoal: {
    type: Object as PropType<ImpactGoalLite>,
    default: null,
  },
},
  emits: ["input", "updated"],
  setup(__props, { emit }: { emit: ({
  (e: 'input', v: boolean): void
  (e: 'updated'): void
}), expose: any, slots: any, attrs: any }) {

const props = __props;





const { proxy } = getCurrentInstance()!
const { mutate: batchLockImpacts, loading } = useBatchLockImpactsMutation()
const ventureId = computed(() => props.impactGoal?.ventureId || '')
const { result, refetch: refetchImpactGoal } = useGetImpactGoalLockStatesQuery(() => ({
  ventureId: ventureId.value,
}), () => ({
  enabled: props.value && Boolean(ventureId.value),
  fetchPolicy: 'network-only',
}))

const lockOperation = ref<'FREEZE' | 'UNFREEZE'>('FREEZE')
const lockReason = ref('')
const showReasonValidation = ref(false)
const showSelectionValidation = ref(false)
const selectedImpactIds = ref<number[]>([])

const trimmedLockReason = computed(() => lockReason.value.trim())
const hasReason = computed(() => Boolean(trimmedLockReason.value))
const lockReasonErrors = computed(() =>
  showReasonValidation.value && !hasReason.value
    ? [String(proxy.$t('reasonRequired'))]
    : [],
)
const selectionErrors = computed(() =>
  showSelectionValidation.value && selectedImpactIds.value.length === 0
    ? [String(proxy.$t('selectAtLeastOne'))]
    : [],
)

const impactRows = computed(() =>
  (
    result.value?.getImpactGoal?.impacts ||
    props.impactGoal?.impacts ||
    []
  ).map((impact, index) => ({
    id: impact.id,
    label: impact.concept?.label || `Impact ${index + 1}`,
    isEditable: impact.isEditable ?? true,
    submitDate: impact.submitDate || null,
  })),
)

watch(
  () => props.value,
  (open) => {
    if (!open) return
    lockOperation.value = 'FREEZE'
    lockReason.value = ''
    showReasonValidation.value = false
    showSelectionValidation.value = false
    refetchImpactGoal()
  },
)

watch(
  impactRows,
  (rows) => {
    selectedImpactIds.value = rows
      .map((row) => row.id)
      .filter((id): id is number => id !== null && id !== undefined)
  },
  { immediate: true },
)

function toggleImpactSelection(impactId: number, selected: boolean) {
  if (selected) {
    if (!selectedImpactIds.value.includes(impactId)) {
      selectedImpactIds.value = [...selectedImpactIds.value, impactId]
    }
    return
  }
  selectedImpactIds.value = selectedImpactIds.value.filter((id) => id !== impactId)
}

const submit = async () => {
  showReasonValidation.value = true
  showSelectionValidation.value = true
  if (!hasReason.value) return
  if (!selectedImpactIds.value.length) return

  await batchLockImpacts({
    input: {
      impactIds: selectedImpactIds.value,
      operation: lockOperation.value,
      lockingLevel: 'PORTFOLIO',
      reason: trimmedLockReason.value,
    },
  })
  await refetchImpactGoal()
  emit('updated')
  emit('input', false)
}

return { __sfc: true,props, emit, proxy, batchLockImpacts, loading, ventureId, result, refetchImpactGoal, lockOperation, lockReason, showReasonValidation, showSelectionValidation, selectedImpactIds, trimmedLockReason, hasReason, lockReasonErrors, selectionErrors, impactRows, toggleImpactSelection, submit, formatAsGermanDate }
}

})